/**
 * Layout system — types + metadata.
 *
 * **Rozróżnienie:**
 *   - **Page layouts** (homepage + statyczne strony bez catalog-content) — wszystkie
 *     obszary to "window areas" (admin może przeciągać okna do każdego)
 *   - **Catalog layouts** (Faza F.1+, podstrony z `[...slug]` routingu) — jeden
 *     dedykowany obszar to "catalog content" (renderuje content katalogu z bazy),
 *     pozostałe obszary to window areas
 *
 * Faza G (now): tylko page layouts dla homepage.
 * Faza F.1: catalog routing + catalog layouts.
 *
 * @see ../../.claude/CLAUDE.md sekcja "Architektura layoutów"
 */

/** Symbol layoutu strony (homepage / static page bez catalog content). */
export type PageLayoutSymbol =
  | "LSingleColumn"
  | "LTwoColsLeft"
  | "LTwoColsRight"
  | "LThreeCols";

/**
 * Symbol obszaru (slot wewnątrz layoutu).
 *
 * **Homepage body areas** (zależne od wybranego page-layout):
 *   - `main`         — główny obszar (zawsze obecny we wszystkich layoutach)
 *   - `leftSidebar`  — w LTwoColsLeft, LThreeCols
 *   - `rightSidebar` — w LTwoColsRight, LThreeCols
 *   - `heroLarge` / `heroSmallTop` / `heroSmallBottom` — opcjonalny hero
 *     split nad `main` w LSingleColumn (1 duży po lewej + 2 małe po prawej).
 *     Puste hero strefy zwijają się do zera (CSS `:has(article)` gate),
 *     więc statyczne strony używające LSingleColumn bez okien hero
 *     wyglądają identycznie jak przed dodaniem split'u.
 *
 * **Catalog body areas** (catalog subpages — NIE homepage):
 *   - `belowCatalog` — window area POD catalog content
 *   - (catalog content sam w sobie NIE jest window area — to blocked obszar
 *     renderowany przez `<CatalogContentArea />`, content przychodzi z
 *     zarządzania katalogiem, NIE z drag&drop'u okien)
 *
 * **Header areas** (zawsze obecne, niezależne od body layout):
 *   - `headerCol1` — pierwsza kolumna nagłówka (prawa strona, najbardziej lewa z 4)
 *   - `headerCol2` — druga kolumna
 *   - `headerCol3` — trzecia kolumna
 *   - `headerCol4` — czwarta kolumna (skrajna prawa)
 *   - `headerBottom` — drugi wiersz nagłówka, full-width pod logo + 4 kolumnami
 *     + theme toggle (np. menu główne, breadcrumbs, search bar)
 *
 *   Wszystkie 4 kolumny wyrównane do prawej, kontener max 50% szerokości
 *   nagłówka. `headerBottom` rozciąga się na całą szerokość headera.
 *
 * **Footer areas** (zawsze obecne):
 *   - `footerLeft`   — lewa kolumna stopki
 *   - `footerMiddle` — środkowa kolumna stopki
 *   - `footerRight`  — prawa kolumna stopki
 *
 * Faza F.2+: header i footer staną się prawdziwymi sub-layoutami z
 * `imp_cms_layout.type='header'/'footer'`. Na razie hardcoded areas.
 */
export type AreaSymbol =
  | "main"
  | "leftSidebar"
  | "rightSidebar"
  | "aboveCatalog"
  | "belowCatalog"
  | "heroLarge"
  | "heroSmallTop"
  | "heroSmallBottom"
  | "headerCol1"
  | "headerCol2"
  | "headerCol3"
  | "headerCol4"
  | "headerBottom"
  | "footerLeft"
  | "footerMiddle"
  | "footerRight";

export type AreaMeta = {
  symbol: AreaSymbol;
  /** PL label widoczny w UI panelu "Układ i okna". */
  name: string;
};

export type PageLayoutMeta = {
  symbol: PageLayoutSymbol;
  /** PL label dla dropdownu w panelu "Układ i okna". */
  name: string;
  areas: AreaMeta[];
};

/**
 * Page layouts — backward-compat re-export z templatu `default`.
 *
 * **Po cutover 2026-05-27 (IV)** literal data żyją per template w
 * `components/cms/registry/templates/{symbol}/layouts.ts`. Ten eksport jest
 * **alias do template `default`** zachowanym dla 12+ istniejących callers
 * (`AlignmentChecker`, `LayoutAndWindowsPanel`, registry helpers itp.) którzy
 * dalej importują `PAGE_LAYOUTS` bezpośrednio.
 *
 * **Nowe callery** powinny używać `getHomepageLayoutsForTemplate(template)`
 * z `@/components/cms/registry/layouts` żeby dispatch był template-aware.
 *
 * @deprecated Use `getHomepageLayoutsForTemplate(template)` z
 *             `@/components/cms/registry/layouts`.
 */
export { homepageLayouts as PAGE_LAYOUTS } from "@/components/cms/registry/templates/default/layouts";

import { homepageLayouts as _defaultPageLayouts } from "@/components/cms/registry/templates/default/layouts";

export const DEFAULT_PAGE_LAYOUT: PageLayoutSymbol = "LSingleColumn";

/**
 * Helpery walidacji symbolu — używają template `default` (legacy fallback).
 * Nowe callery powinny używać `isPageLayoutSymbolForTemplate(value, template)` z
 * `@/components/cms/registry/layouts` dla template-aware walidacji.
 */
export function isPageLayoutSymbol(value: unknown): value is PageLayoutSymbol {
  return _defaultPageLayouts.some((l) => l.symbol === value);
}

export function getPageLayoutMeta(symbol: PageLayoutSymbol): PageLayoutMeta {
  const meta = _defaultPageLayouts.find((l) => l.symbol === symbol);
  if (!meta) throw new Error(`Unknown page layout symbol: ${symbol}`);
  return meta;
}

/* ─── Catalog layouts (subpages — `app/[...slug]/page.tsx`) ────────── */

/** Symbol layoutu podstrony katalogu (z dedykowanym catalog content area). */
export type CatalogLayoutSymbol =
  | "LCatalogSingleColumn"
  | "LCatalogSingleColumnFull"
  | "LCatalogTwoColsLeft"
  | "LCatalogTwoColsRight"
  | "LCatalogThreeCols"
  | "LCatalogLanding";

export type CatalogLayoutMeta = {
  symbol: CatalogLayoutSymbol;
  /** PL label dla dropdownu w panelu "Układ i okna". */
  name: string;
  /**
   * Areas DOSTĘPNE dla drag&drop w tym layoucie. NIE zawiera `catalogContent`
   * (to blocked area, renderowane przez `<CatalogContentArea />`, NIE drop target).
   */
  areas: AreaMeta[];
  /**
   * Gdy `true`, PublicLayoutView NIE renderuje chrome (HDefault header +
   * FDefault footer + top message). Layout odpowiedzialny za pełne
   * "full bleed" rendering (np. landing page). Default `false` - chrome
   * widoczne zgodnie z normalnym layoutem strony.
   */
  hideChrome?: boolean;
};

/**
 * Catalog layouts — backward-compat re-export z templatu `default`.
 *
 * Po cutover 2026-05-27 (IV) literal data per template w
 * `components/cms/registry/templates/{symbol}/layouts.ts`. Nowe callery używają
 * `getCatalogLayoutsForTemplate(template)` z `@/components/cms/registry/layouts`.
 *
 * @deprecated Use `getCatalogLayoutsForTemplate(template)`.
 */
export { catalogLayouts as CATALOG_LAYOUTS } from "@/components/cms/registry/templates/default/layouts";

import { catalogLayouts as _defaultCatalogLayouts } from "@/components/cms/registry/templates/default/layouts";

export const DEFAULT_CATALOG_LAYOUT: CatalogLayoutSymbol = "LCatalogSingleColumn";

/**
 * Helpery walidacji symbolu — używają template `default` (legacy fallback).
 * Nowe callery: `isCatalogLayoutSymbolForTemplate(value, template)` z
 * `@/components/cms/registry/layouts` dla template-aware walidacji.
 */
export function isCatalogLayoutSymbol(value: unknown): value is CatalogLayoutSymbol {
  return _defaultCatalogLayouts.some((l) => l.symbol === value);
}

export function getCatalogLayoutMeta(symbol: CatalogLayoutSymbol): CatalogLayoutMeta {
  const meta = _defaultCatalogLayouts.find((l) => l.symbol === symbol);
  if (!meta) throw new Error(`Unknown catalog layout symbol: ${symbol}`);
  return meta;
}

/* ─── Union / picker — homepage vs catalog ─────────────────────────── */

/** Discriminated union — picker per page type. */
export type LayoutSymbol = PageLayoutSymbol | CatalogLayoutSymbol;

export function isLayoutSymbol(value: unknown): value is LayoutSymbol {
  return isPageLayoutSymbol(value) || isCatalogLayoutSymbol(value);
}

/* ─── Chrome areas (zawsze dostępne, niezależne od page-layout) ────── */

/**
 * Areas chrome'u strony — header + footer. ZAWSZE renderowane przez `<HDefault />`
 * i `<FDefault />` niezależnie od wybranego page-layout (LSingleColumn / LTwoColsLeft /
 * LCatalogSingleColumn / etc.). Stąd okna z `allowedAreas` zawierającymi te
 * symbole są kompatybilne ze wszystkimi layoutami.
 *
 * Body areas (`main`, `leftSidebar`, `rightSidebar`, `belowCatalog`)
 * są zależne od layoutu — definicja w `PAGE_LAYOUTS[i].areas` lub `CATALOG_LAYOUTS[i].areas`.
 */
export const CHROME_AREAS: AreaSymbol[] = [
  "headerCol1",
  "headerCol2",
  "headerCol3",
  "headerCol4",
  "headerBottom",
  "footerLeft",
  "footerMiddle",
  "footerRight",
];

/**
 * Pełna lista obszarów dostępnych na stronie z wybranym layoutem.
 * = layout body areas + chrome areas (header/footer constant).
 *
 * Używane przez `LayoutAndWindowsPanel` do sprawdzenia kompatybilności okna
 * (intersekcja z `window.allowedAreas`).
 */
export function getAvailableAreasForLayout(
  layoutMeta: PageLayoutMeta | CatalogLayoutMeta,
): AreaSymbol[] {
  return [...layoutMeta.areas.map((a) => a.symbol), ...CHROME_AREAS];
}

/* ─── Per-page state cookie names ──────────────────────────────────── */

/**
 * Cookie z mapą `{ slug -> LayoutSymbol }`. Slug "/" dla homepage.
 *
 * **Faza G.9 zmiana:** zastępuje single `cms_homepage_layout` cookie. Każda
 * podstrona ma teraz osobny entry. Migration: stare cookie ignorowane.
 */
export const COOKIE_LAYOUTS = "cms_layouts";
